/**
 * Formats a string for use in tracking.
 *
 * This function removes diacritical marks, whitespace, and other characters
 * that should not be included in tracking strings.
 *
 * @version 1.9 - converlytics tealium concept
 */

export function normalizeTrackingValue<T extends string | undefined>(
  value: T,
): T extends string ? string : undefined {
  if (typeof value === 'undefined') {
    return undefined as T extends string ? string : undefined;
  }

  const normalizedValue = (value ?? '')
    // always lowercase the string
    .toLowerCase()

    // replace german umlauts before normalizing the string to NFKD to match the desired format
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')

    // normalize the string to NFKD to remove diacritical marks
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // format diacritical marks

    // replace any whitespaces with hyphens
    .replace(/\s+/g, '-')

    // TODO: What will be the result of 9.123,44 ?
    // TODO: This produces issues, when using sentences with commas: "Please, set a password."
    // replace commas with periods (decimal places)
    // .replace(/,/g, '.')

    // replace all remaining non-alphanumeric characters with underscores
    .replace(/[^a-z0-9-.]/g, '_')

    // reduce multiple hyphens and underscores to just one hyphen
    .replace(/[-_]{2,}/g, '-')

    // remove leading and trailing hyphens and underscores
    .replace(/[-_]+$/g, '')
    .replace(/^[-_]+/g, '');

  return normalizedValue as T extends string ? string : undefined;
}

export const formatTrackingString = normalizeTrackingValue;
